import React, { useEffect, useRef, useState } from 'react'
import { Container } from './atoms'
import { TooltipElement } from './tooltip-element'

export type Placement = 'left' | 'top' | 'bottom' | 'right' | 'bottom-left'

interface Props {
  children: React.ReactElement
  content: React.ReactNode
  placement?: Placement
  background?: string
  borderColor?: string
  borderRadius?: number
  gap?: number
  gapMb?: number
  handleShow?: (v: boolean) => void
}

export const Tooltip = ({
  children,
  content,
  placement = 'bottom',
  background = '#747476',
  borderColor = '#838385',
  borderRadius = 4,
  gap = 16,
  gapMb = 26,
  handleShow,
}: Props) => {
  const [isWindow, setIsWindow] = useState(false)
  const [show, setShow] = useState(false)
  const childRef = useRef<HTMLElement | null>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [containerHeight, setContainerHeight] = useState(0)
  const [containerTop, setContainerTop] = useState(0)
  const [containerLeft, setContainerLeft] = useState(0)
  const closeTimeout = useRef(0)

  useEffect(() => {
    setIsWindow(true)
    if (isWindow) {
      if (childRef.current) {
        const { height, width, top, left } = childRef.current.getBoundingClientRect()
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
        setContainerWidth(width)
        setContainerHeight(height)
        setContainerTop(top + scrollTop)
        setContainerLeft(left + scrollLeft)
      }
    }
  }, [isWindow, show])

  const handleClose = () =>
    (closeTimeout.current = window.setTimeout(() => {
      setShow(false)
      handleShow?.(false)
    }, 200))

  const childrenWithProps = React.Children.map(children, (child) =>
    React.cloneElement(child, {
      ref: childRef,
      onMouseEnter: () => {
        clearTimeout(closeTimeout.current)
        setShow(true)
        handleShow?.(true)
      },
      onMouseLeave: handleClose,
    }),
  )

  return isWindow && !Array.isArray(children) ? (
    <Container>
      {childrenWithProps}
      <TooltipElement
        show={show}
        content={content}
        closeTimeout={closeTimeout}
        handleClose={handleClose}
        placement={placement}
        background={background}
        borderColor={borderColor}
        borderRadius={borderRadius}
        containerWidth={containerWidth}
        containerHeight={containerHeight}
        containerTop={containerTop}
        containerLeft={containerLeft}
        gap={gap}
        gapMb={gapMb}
      />
    </Container>
  ) : null
}
